import {ChangeDetectionStrategy, Component, inject, input, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {Button} from 'primeng/button';
import {Textarea} from 'primeng/textarea';
import {MessageService} from 'primeng/api';

import {SubmitButtonComponent} from '../../shared/components/submit-button/submit-button.component';
import {CopyButtonComponent} from '../../shared/components/copy-button/copy-button.component';
import {silentErrors} from '../../shared/http-context';
import {NotificationsClient} from '../../api-client/api/api';
import {SendNotificationCommandDto} from '../../api-client/model/send-notification-command-dto';

const DEFAULT_PAYLOAD = JSON.stringify(
  {
    type: 'custom',
    data: {
      textMessage: 'Test notification from admin',
      timeSensitive: true,
    },
  },
  null,
  2
);

/**
 * One entry of the `details` array the API puts on `ErrorDto` for a 400: typia's per-field
 * validation error, `{path, expected, value}`. Only path and expected are rendered - `value`
 * is the operator's own input and is already on screen in the textarea.
 */
export type ValidationDetail = {
  readonly path: string;
  readonly expected: string;
};

function readProperty(value: object, key: string): unknown {
  for (const [entryKey, entryValue] of Object.entries(value)) {
    if (entryKey === key) {
      return entryValue;
    }
  }
  return undefined;
}

function readStringProperty(value: object, key: string): string | undefined {
  const property = readProperty(value, key);
  return typeof property === 'string' ? property : undefined;
}

function toValidationDetail(value: unknown): ValidationDetail | undefined {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const path = readStringProperty(value, 'path');
  const expected = readStringProperty(value, 'expected');
  if (path === undefined || expected === undefined) {
    return undefined;
  }

  return {path, expected};
}

function extractValidationDetails(payload: unknown): readonly ValidationDetail[] {
  if (typeof payload !== 'object' || payload === null) {
    return [];
  }

  const details = readProperty(payload, 'details');
  if (!Array.isArray(details)) {
    return [];
  }

  const parsed: ValidationDetail[] = [];
  for (const entry of details) {
    const detail = toValidationDetail(entry);
    if (detail !== undefined) {
      parsed.push(detail);
    }
  }
  return parsed;
}

function hasInformationKey(value: unknown): value is {information: unknown} {
  return typeof value === 'object' && value !== null && 'information' in value;
}

function isRecordWithMessage(value: unknown): value is {message: string} {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if (!('message' in value)) {
    return false;
  }
  return typeof value.message === 'string';
}

@Component({
  selector: 'homey-send-text-message-page',
  standalone: true,
  imports: [Button, Textarea, SubmitButtonComponent, CopyButtonComponent],
  templateUrl: './send-text-message-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendTextMessagePageComponent {
  public readonly userId = input.required<string>();

  private readonly router = inject(Router);
  private readonly notificationsClient = inject(NotificationsClient);
  private readonly messageService = inject(MessageService);

  protected readonly jsonInput = signal(DEFAULT_PAYLOAD);
  protected readonly validationError = signal<string | undefined>(undefined);
  protected readonly validationDetails = signal<readonly ValidationDetail[]>([]);
  protected readonly notificationId = signal<string | undefined>(undefined);

  protected onJsonInput(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLTextAreaElement) {
      this.jsonInput.set(target.value);
    }
  }

  protected goBack(): void {
    this.router.navigate(['/users', this.userId()]);
  }

  protected resetJson(): void {
    this.validationError.set(undefined);
    this.validationDetails.set([]);
    this.jsonInput.set(DEFAULT_PAYLOAD);
  }

  protected readonly sendTextMessage = async (): Promise<void> => {
    this.validationError.set(undefined);
    this.validationDetails.set([]);
    this.notificationId.set(undefined);

    let parsedPayload: unknown;
    try {
      parsedPayload = JSON.parse(this.jsonInput());
    } catch {
      this.validationError.set('Provided value is not valid JSON.');
      return;
    }

    const command: SendNotificationCommandDto = hasInformationKey(parsedPayload)
      ? {information: parsedPayload.information}
      : {information: parsedPayload};

    try {
      const result = await firstValueFrom(
        // silentErrors: a rejected payload is rendered inline below, field by field, with the
        // typia path that failed. A toast saying "Request failed - 400" adds nothing to that.
        this.notificationsClient.sendUserNotification(this.userId(), command, 'body', false, {
          context: silentErrors(),
        })
      );
      this.notificationId.set(result.notificationId);
      this.messageService.add({
        severity: 'success',
        summary: 'Notification created',
        detail: `Notification ID ${result.notificationId}`,
      });
    } catch (error) {
      this.validationError.set(this.getErrorMessage(error));
      this.validationDetails.set(this.getErrorDetails(error));
    }
  };

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.normalizeServerMessage(error.error);
      if (serverMessage) {
        return serverMessage;
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unable to send text message.';
  }

  private getErrorDetails(error: unknown): readonly ValidationDetail[] {
    if (error instanceof HttpErrorResponse) {
      return extractValidationDetails(error.error);
    }

    return [];
  }

  private normalizeServerMessage(payload: unknown): string | undefined {
    if (typeof payload === 'string' && payload.length > 0) {
      return payload;
    }

    if (isRecordWithMessage(payload)) {
      return payload.message;
    }

    return undefined;
  }
}
